/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * Numpad.web.tsx - Numpad view for the app.
 */

import React, { useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import * as colours from '../../../colours';
import './Numpad.web.css';


const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;
type NumberCharacter = typeof numbers[number] | '0';

interface NumpadProps {
  handleInputEvent(input: NumberCharacter): void;
  handleBackspaceEvent(): void;
  handleConfirmEvent(): void;
  disabled: boolean;
}

const Numpad = ({ handleInputEvent, handleBackspaceEvent, handleConfirmEvent, disabled }: NumpadProps) => {
  // Handle keyboard events.
  const onKeyboardDown = useCallback(
    (event: KeyboardEvent) =>
      handleKeyboardDown(event, disabled, handleInputEvent, handleBackspaceEvent, handleConfirmEvent),
    [disabled, handleInputEvent, handleBackspaceEvent, handleConfirmEvent]
  );
  useEffect(() => {
    document.addEventListener('keydown', onKeyboardDown);
    return () => document.removeEventListener('keydown', onKeyboardDown);
  }, [onKeyboardDown]);

  // Callback for handling numpad button clicks, used later
  const onScreenButtonPress = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, action: ScreenButtonPressAction) =>
      handleScreenButtonPress(event, action, handleInputEvent, handleBackspaceEvent, handleConfirmEvent),
    [handleInputEvent, handleBackspaceEvent, handleConfirmEvent]
  );

  return (
    <View style={styles.pad}>
      {numbers.map((value, index) => {
        return (
          <button
            key={index}
            className="button"
            onClick={event => onScreenButtonPress(event, { code: NUMBER, value })}
            onFocus={() => document.getElementById('questions_content')?.focus()}
            disabled={disabled}
          >
            <p className="numpad_button_text">{value}</p>
          </button>
        );
      })}
      <button
        key="backspace"
        className="button"
        onClick={event => onScreenButtonPress(event, { code: DELETE })}
        disabled={disabled}
      >
        <img
          src={require('../../../assets/images/DeleteButton_72dpi.png')}
          style={{
            height: '60%',
            aspectRatio: '1',
          }}
        />
      </button>

      <button
        key="zero"
        className="button"
        onClick={event => onScreenButtonPress(event, { code: NUMBER, value: '0' })}
        disabled={disabled}
      >
        <p className="numpad_button_text">0</p>
      </button>

      <button
        key="confirm"
        className="button"
        onClick={event => onScreenButtonPress(event, { code: ENTER })}
        disabled={disabled}
      >
        <img
          src={require('../../../assets/images/OKButton_72dpi.png')}
          style={{
            height: '60%',
            aspectRatio: '1',
          }}
        />
      </button>
    </View>
  );
};

export default Numpad;

const NUMBER = 'NUMBER';
const DELETE = 'DELETE';
const ENTER = 'ENTER';

type ScreenButtonPressAction = 
  | { code: typeof DELETE; }
  | { code: typeof ENTER; }
  | { code: typeof NUMBER; value: NumberCharacter };

const handleScreenButtonPress = (
  event: React.MouseEvent,
  action: ScreenButtonPressAction,
  handleInputEvent: NumpadProps['handleInputEvent'],
  handleBackspaceEvent: NumpadProps['handleBackspaceEvent'],
  handleConfirmEvent: NumpadProps['handleConfirmEvent']
) => {
  if (event.type === 'keydown') {
    return;
  }

  if (event.clientX === 0 && event.clientY === 0) {
    return;
  }

  if (action.code === NUMBER) {
    handleInputEvent(action.value);
  } else if (action.code === DELETE) {
    handleBackspaceEvent();
  } else if (action.code === ENTER) {
    handleConfirmEvent();
  }
};

const handleKeyboardDown = (
  event: KeyboardEvent,
  disabled: boolean,
  handleInputEvent: NumpadProps['handleInputEvent'],
  handleBackspaceEvent: NumpadProps['handleBackspaceEvent'],
  handleConfirmEvent: NumpadProps['handleConfirmEvent']
) => {
  if (event.code === 'Tab') {
    event.stopPropagation();
    event.preventDefault();
    return;
  }
  if (disabled) {
    return;
  }
  if (event.code === 'Enter' || event.code === 'NumpadEnter') {
    handleConfirmEvent();
  } else if (event.code === 'Backspace' || event.code === 'Delete') {
    handleBackspaceEvent();
  } else {
    let value: NumberCharacter;
    switch (event.code) {
      case 'Digit1':
      case 'Numpad1':
      {
        value = '1';
        break;
      }
      case 'Digit2':
      case 'Numpad2':
      {
        value = '2';
        break;
      }
      case 'Digit3':
      case 'Numpad3':
      {
        value = '3';
        break;
      }
      case 'Digit4':
      case 'Numpad4':
      {
        value = '4';
        break;
      }
      case 'Digit5':
      case 'Numpad5':
      {
        value = '5';
        break;
      }
      case 'Digit6':
      case 'Numpad6':
      {
        value = '6';
        break;
      }
      case 'Digit7':
      case 'Numpad7':
      {
        value = '7';
        break;
      }
      case 'Digit8':
      case 'Numpad8':
      {
        value = '8';
        break;
      }
      case 'Digit9':
      case 'Numpad9':
      {
        value = '9';
        break;
      }
      case 'Digit0':
      case 'Numpad0':
      {
        value = '0';
        break;
      }
      default: {
        return;
      }
    }

    handleInputEvent(value);
  }
};

const styles = StyleSheet.create({
  pad: {
    flexWrap: 'wrap',
    flexDirection: 'row',
    height: '100%',
  },

  button: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '33%',
    height: '25%',
  },

  buttonText: {
    color: '#888',
    fontSize: 80,
    textAlign: 'center',
    fontFamily: 'WhiteRoseMathsFont',
  },

  confirmText: {
    color: colours.confirm
  },

  deleteText: {
    color: colours.backspace
  }

});
